import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthUser, ChangePasswordInput, ChangePasswordResult, Permission, UpdateProfileInput } from '@quds/shared';
import { invalidate } from '@/data/invalidation';
import { ApiError, apiGet, apiPatch, apiPost, onUnauthorized } from '@/lib/api';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** True when the previous session ended (expired, revoked or deactivated) rather than a normal sign-out. */
  sessionExpired: boolean;
  signIn: (email: string, password: string, remember: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  /** Changes the signed-in admin's password; other sessions are signed out by the API. */
  changePassword: (input: ChangePasswordInput) => Promise<ChangePasswordResult>;
  updateDisplayName: (displayName: string) => Promise<void>;
  /** Re-reads the signed-in user (e.g. after editing yourself in Admin Users). */
  refreshUser: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Server-side sessions: the API sets an HttpOnly cookie on sign-in; the
 * browser never sees the token. On load the session is restored via /auth/me.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let active = true;
    apiGet<{ user: AuthUser }>('/auth/me')
      .then(({ user: me }) => {
        if (!active) return;
        setUser(me);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
        setStatus('anonymous');
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(
    () =>
      onUnauthorized(() => {
        setUser((current) => {
          if (current) setSessionExpired(true);
          return null;
        });
        setStatus('anonymous');
      }),
    [],
  );

  const signIn = useCallback(async (email: string, password: string, remember: boolean) => {
    const { user: me } = await apiPost<{ user: AuthUser }>('/auth/login', { email: email.trim(), password, remember });
    setSessionExpired(false);
    setUser(me);
    setStatus('authenticated');
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiPost<void>('/auth/logout');
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    } finally {
      setSessionExpired(false);
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const { user: me } = await apiGet<{ user: AuthUser }>('/auth/me');
    setUser(me);
  }, []);

  const changePassword = useCallback(
    async (input: ChangePasswordInput) => {
      const result = await apiPatch<ChangePasswordResult>('/auth/password', input);
      await refreshUser();
      return result;
    },
    [refreshUser],
  );

  const updateDisplayName = useCallback(async (displayName: string) => {
    const { user: me } = await apiPatch<{ user: AuthUser }>('/auth/profile', { displayName } satisfies UpdateProfileInput);
    setUser(me);
    invalidate();
  }, []);

  const can = useCallback((permission: Permission) => !!user?.permissions.includes(permission), [user]);

  const value = useMemo(
    () => ({ status, user, sessionExpired, signIn, signOut, changePassword, updateDisplayName, refreshUser, can }),
    [status, user, sessionExpired, signIn, signOut, changePassword, updateDisplayName, refreshUser, can],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** The signed-in user; only call inside authenticated routes. */
export function useCurrentUser(): AuthUser {
  const { user } = useAuth();
  if (!user) throw new Error('No authenticated user');
  return user;
}

/** Permission check for UI gating. The API enforces the same rules. */
export function useCan(): (permission: Permission) => boolean {
  return useAuth().can;
}
